import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Mail } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";

export default function Terms() {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="terms-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-10 md:pb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="terms-back"
          >
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <FileText className="w-3 h-3 text-[#C9A349]" />
            Engagement terms · NZ Taxation Limited
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]"
            data-testid="terms-headline"
          >
            Terms &amp; <span className="text-[#C9A349]">Conditions.</span>
          </motion.h1>

          <p
            className="mt-5 text-sm text-slate-500 uppercase tracking-[0.18em] font-bold"
            data-testid="terms-updated"
          >
            Last updated: 25 May 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white border-b border-slate-200">
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <Lede>
            These Terms &amp; Conditions set out the agreement between NZ Taxation Limited
            (<strong>"we"</strong>, <strong>"us"</strong>, <strong>"our"</strong>) and you
            (<strong>"client"</strong>, <strong>"you"</strong>) when you engage us for
            accounting, tax, bookkeeping, payroll or advisory services through{" "}
            <a
              href="https://www.nztaxation.nz"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              https://www.nztaxation.nz
            </a>
            . By signing up, paying for a subscription, or otherwise using our services, you
            confirm that you have read, understood and agreed to these Terms.
          </Lede>

          <H2 n={1}>About us</H2>
          <P>
            NZ Taxation Limited is a New Zealand-incorporated company providing accounting and
            tax services. We are IRD-registered tax agents and Xero Certified Advisors. Our
            registered office is in Te Atatu, Auckland. We can be reached at{" "}
            <a
              href="mailto:info@nztaxation.nz"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              info@nztaxation.nz
            </a>
            .
          </P>

          <H2 n={2}>Our services</H2>
          <P>
            We provide tax compliance, financial statement preparation, bookkeeping, payroll
            processing, monthly tax estimates, and related advisory services. The specific
            services we provide to you are outlined in your selected plan, our onboarding
            confirmation, and any written engagement scope agreed between us.
          </P>
          <P>
            Where the work falls outside the agreed scope (e.g. one-off projects, prior-year
            catch-up returns, IRD correspondence beyond routine compliance), we will quote
            those items separately before commencing work.
          </P>

          <H2 n={3}>Your responsibilities</H2>
          <P>To allow us to deliver our services accurately and on time, you agree to:</P>
          <BulletList>
            <li>
              Provide complete, accurate and timely information, including bank statements,
              invoices, receipts, and any other records relevant to your tax position.
            </li>
            <li>
              Authorise us as your tax agent with Inland Revenue, and as a user of any
              accounting software (e.g. Xero, Prosaic) you have asked us to manage.
            </li>
            <li>
              Notify us promptly of any changes to your business structure, banking, residency,
              or contact details.
            </li>
            <li>
              Review and approve all tax returns, financial statements, and IRD lodgements
              before they are filed where the law requires your sign-off.
            </li>
          </BulletList>
          <P>
            You acknowledge that we rely on the information you provide and are not responsible
            for outcomes resulting from inaccurate, incomplete or late information supplied by
            you or any third party.
          </P>

          <H2 n={4}>Fees, GST and payment</H2>
          <P>
            Subscription prices listed on our website are in New Zealand dollars and{" "}
            <strong>exclusive of GST</strong>. NZ GST (currently 15%) is added at checkout via
            Stripe Tax. Where applicable, a one-off prior-year catch-up fee may be added to
            your first invoice and is also exclusive of GST.
          </P>
          <P>
            All subscriptions are billed monthly through Stripe. By providing card details, you
            authorise Stripe to charge your nominated payment method automatically each month
            for the duration of your subscription.
          </P>
          <P>
            Out-of-scope or one-off work is invoiced separately and is due within 7 days of the
            invoice date, unless otherwise agreed in writing.
          </P>

          <H2 n={5}>Cancellation and refunds</H2>
          <P>
            You may cancel your monthly subscription at any time by emailing{" "}
            <a
              href="mailto:info@nztaxation.nz"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              info@nztaxation.nz
            </a>
            . Cancellation takes effect at the end of the current billing month, and you will
            retain access to services already paid for until that date.
          </P>
          <P>
            Monthly subscription fees are <strong>non-refundable</strong> once paid, as the
            work for that month is performed continuously throughout the period. Prior-year
            catch-up fees are non-refundable once the related return has been prepared and
            filed. Where a service has been paid for but not yet started, we will refund the
            amount in full at our discretion.
          </P>

          <H2 n={6}>Third-party tools and integrations</H2>
          <P>
            Delivery of our services may involve third-party platforms, including but not
            limited to Inland Revenue (IRD), Xero Limited, Xero Practice Manager, Prosaic,
            Microsoft 365, Stripe (payments), Jotform (onboarding), and various secure cloud
            storage providers. By engaging us, you authorise us to use these platforms on your
            behalf as required to deliver the agreed services.
          </P>
          <P>
            We are not liable for outages, errors, data loss, or delays caused by these
            third-party providers, although we will use reasonable efforts to mitigate the
            impact on your work.
          </P>

          <H2 n={7}>Confidentiality and privacy</H2>
          <P>
            We treat all information you provide as confidential and use it only to deliver
            our services or as required by law. Our handling of personal information is
            governed by our{" "}
            <Link
              to="/privacy"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              Privacy Policy
            </Link>{" "}
            and complies with the Privacy Act 2020 (New Zealand).
          </P>

          <H2 n={8}>Professional standards and limitation of liability</H2>
          <P>
            We deliver our services with the reasonable care, skill and diligence expected of
            an NZ tax agent and Xero Certified Advisor, in accordance with applicable
            professional standards and IRD requirements.
          </P>
          <P>
            To the maximum extent permitted by law, our total liability to you for any claim
            arising out of or in connection with these Terms or the services is{" "}
            <strong>limited to the total fees paid by you to us in the twelve (12) months</strong>{" "}
            preceding the event giving rise to the claim. We are not liable for any indirect,
            consequential, or loss-of-profit damages.
          </P>
          <P>
            Nothing in these Terms is intended to exclude any rights you have under the
            Consumer Guarantees Act 1993 where those rights apply and cannot lawfully be
            excluded.
          </P>

          <H2 n={9}>Termination</H2>
          <P>We may suspend or terminate your access to our services if:</P>
          <BulletList>
            <li>You fail to pay an invoice within 14 days of its due date;</li>
            <li>
              You provide materially false or misleading information, or use our services for
              an unlawful purpose;
            </li>
            <li>
              We are required to do so to comply with the law, including under the Anti-Money
              Laundering and Countering Financing of Terrorism Act 2009.
            </li>
          </BulletList>
          <P>
            On termination, we will provide a clean handover of your records to you or your
            new advisor where reasonably possible, subject to any outstanding fees being paid
            in full.
          </P>

          <H2 n={10}>Intellectual property</H2>
          <P>
            Templates, processes, working papers, and other materials we create in delivering
            the services remain our intellectual property. You retain ownership of your own
            financial records and documents that you provide to us. Final outputs (e.g. your
            tax returns and financial statements) belong to you once paid for.
          </P>

          <H2 n={11}>Communications</H2>
          <P>
            We will communicate with you primarily by email, including delivery of your
            monthly tax position. You are responsible for keeping your email address up to
            date and for ensuring our emails are not blocked or filtered by your inbox. Where
            the law requires written notice, email shall constitute written notice unless
            otherwise specified.
          </P>

          <H2 n={12}>Changes to these terms</H2>
          <P>
            We may update these Terms from time to time. The latest version will always be
            published at{" "}
            <a
              href="https://www.nztaxation.nz/terms"
              className="text-[#0A192F] underline underline-offset-4 hover:text-[#C9A349]"
            >
              https://www.nztaxation.nz/terms
            </a>{" "}
            with the date of the most recent update shown above. Material changes will be
            notified to active clients by email at least 14 days before they take effect.
          </P>

          <H2 n={13}>Governing law</H2>
          <P>
            These Terms are governed by the laws of New Zealand. Any disputes arising under or
            in connection with these Terms will be resolved in the courts of New Zealand,
            unless we both agree in writing to refer the matter to mediation or arbitration in
            Auckland first.
          </P>

          <H2 n={14}>Contact us</H2>
          <P>For any questions about these Terms or your engagement, please contact:</P>

          <div className="mt-6 border border-slate-200 bg-slate-50 p-6 md:p-7">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
              NZ Taxation Limited
            </div>
            <div className="mt-2 font-heading font-black text-xl text-[#0A192F]">
              Michael Amador
            </div>
            <div className="text-sm text-slate-600">Director</div>
            <a
              href="mailto:info@nztaxation.nz"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#0A192F] hover:text-[#C9A349] transition-colors"
              data-testid="terms-contact-email"
            >
              <Mail className="w-4 h-4" /> info@nztaxation.nz
            </a>
          </div>
        </article>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

/* ---------- Building blocks ---------- */

function H2({ n, children }) {
  return (
    <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-12 mb-3 text-[#0A192F] leading-tight scroll-mt-24">
      <span className="text-[#C9A349]">{String(n).padStart(2, "0")}.</span> {children}
    </h2>
  );
}

function P({ children }) {
  return <p className="text-base md:text-[17px] leading-[1.75] text-slate-700 my-4">{children}</p>;
}

function BulletList({ children }) {
  const arr = Array.isArray(children) ? children : [children];
  return (
    <ul className="list-none p-0 my-4 space-y-2.5 text-base md:text-[17px] text-slate-700">
      {arr.map((item, i) => (
        <li key={i} className="flex items-start gap-3 leading-relaxed">
          <span className="w-1.5 h-1.5 bg-[#C9A349] flex-shrink-0 mt-2.5" />
          <span>{item.props ? item.props.children : item}</span>
        </li>
      ))}
    </ul>
  );
}

function Lede({ children }) {
  return (
    <div className="border-l-4 border-[#C9A349] bg-[#FAF6EC] px-5 md:px-7 py-5 md:py-6 text-base md:text-lg text-slate-700 leading-relaxed">
      {children}
    </div>
  );
}
